import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { PortalMe } from "../models/PortalMe";
import { selectPortalSession, setPortalSelectedMember } from "../portal-slice";
import { findContextOffer } from "../utils/findContextOffer";

/** The search parameter that carries the guardian's pick (self or a ward) on every portal route. */
export const PORTAL_MEMBER_PARAM = "member";

export interface PortalMemberSelection {
  /** The member the page shows: the URL's pick when it is self or a ward, else self; null until `me` loads. */
  memberId: string | null;
  /** Shows another member (self or a ward) on the current page, replacing the URL. */
  selectMember: (memberId: string) => void;
}

/**
 * The one reader and writer of `?member=`. The pick is validated against `me.self` and `me.wards`;
 * an absent pick means self, a foreign one is dropped from the URL (replace) and self is shown, so
 * nothing is fetched for it. A pick that is another row of the same email (PD-0033) stays in the URL
 * and shows nobody: the portal chrome offers the switch (PortalContextOffer). The valid pick is
 * mirrored into the store for the header's unread count.
 */
export const usePortalMember = (me: PortalMe | null): PortalMemberSelection => {
  const dispatch = useAppDispatch();
  const [params, setParams] = useSearchParams();
  const requested = params.get(PORTAL_MEMBER_PARAM);
  const selfId = me?.self.memberId ?? null;
  const contexts = useAppSelector(selectPortalSession)?.contexts ?? [];
  const allowed = me ? [me.self, ...me.wards].some((member) => member.memberId === requested) : false;
  const offered = findContextOffer(me, requested, contexts) !== null;
  const memberId = me && !offered ? (allowed ? requested : selfId) : null;

  useEffect(() => {
    if (!me || requested === null || allowed || offered) return;
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(PORTAL_MEMBER_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [me, requested, allowed, offered, setParams]);

  useEffect(() => {
    if (memberId) dispatch(setPortalSelectedMember(memberId));
  }, [memberId, dispatch]);

  const selectMember = useCallback(
    (id: string) =>
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (id === selfId) next.delete(PORTAL_MEMBER_PARAM);
          else next.set(PORTAL_MEMBER_PARAM, id);
          return next;
        },
        { replace: true },
      ),
    [selfId, setParams],
  );

  return { memberId, selectMember };
};
